import React, { useCallback, useEffect, useMemo } from 'react'
import { ProfileEditAndConfirmationStep, ProfilePictureStep } from '../../steps'
import {
	useModalContext,
	type ModalContextProps,
	StepModal,
	withStepModalProvider,
	BROADCAST_MESSAGES,
	useBroadcastChannel,
} from '@adsk/napi-shared'
import {
	NapiClient,
	OnboardedBuilderNetworkResponse,
} from '@adsk/napi-api-client'
import withConfig, { type Config } from '@adsk/napi-config'
import {
	OnboardingUserAppContextType,
	OnboardingUserAppProvider,
	useOnboardingUserContext,
} from '../../store'
import withBugsnag, { BugsnagNotify } from '@adsk/napi-bugsnag'
import {
	setAppConfig,
	setFlowType,
	setSelectionsList,
	setImmutableUserInfo,
	setUserInfo,
	showLoadingState,
} from '../../store/actions'
import { track } from '@adsk/acs-web-analytics'
import ANALYTICS_TYPES from '../../types/AnalyticsTypes'
import { ONBOARDING_FLOW } from '../../types/ActionTypes'
import { getPackageInfo } from '../../utils'
import { eliminateDefaultXAvatar } from '../../utils/helpers'
import { SELECTION_LIST_KEYS } from '../../constants'
import {
	withMultipleProviders,
	ConditionalErrorBoundary,
} from '@adsk/napi-shared'
import { FeatureFlagProvider, useFeatureFlags } from '@adsk/napi-feature-flags'
import {
	isInExoSkeleton,
	usePerformanceTracking,
	TOTAL_REMOTE_LOADING_TIME,
	useExoTrackReadyPagePerformance,
} from '@adsk/napi-exo-client'
import usePendoAction from '../../hooks/usePendoAction'
import _ from 'lodash'
import { ErrorModal } from '../../components/error-modal/ErrorModalComponent'

const { version, name } = getPackageInfo()
const displayName = 'NAPIC - Onboarding User Modal'

interface OnboardingUserModalAppProps {
	config: Config
}

const OnboardingUserModalApp: React.FC<OnboardingUserModalAppProps> = ({
	config,
}: OnboardingUserModalAppProps) => {
	const {
		startTracking: startIsOnboardedAPITracking,
		endTracking: endIsOnboardedAPITracking,
	} =
		usePerformanceTracking({
			trackingId: 'is-onboarded-to-builders-network-api',
			// this will link this tracking to the total loading time of the remote
			parentTrackingId: TOTAL_REMOTE_LOADING_TIME,
		}) || {}

	// Internal App  Section
	const {
		dispatch,
		externalProps,
		state: { showLoader },
	} = useOnboardingUserContext() as OnboardingUserAppContextType
	const {
		state: { napiUserOnboarding = false },
	} = useFeatureFlags()
	const { openModal, closeModal } = useModalContext() as ModalContextProps
	const { startPendoGuidesPolling } = usePendoAction({
		// Onboarding Modal depends on the Pendo
		onHideCallback: () => {
			openModal()
			dispatch(showLoadingState(false))
		},
	})
	useExoTrackReadyPagePerformance({
		displayName,
		isLoading: napiUserOnboarding && showLoader,
	})

	const { message } = useBroadcastChannel(
		BROADCAST_MESSAGES.USER_CLOSE_ONBOARDING_MODAL
	)

	useEffect(() => {
		// Closes the modal when a separate modal sends a message to close
		// this is not supported on older browsers https://caniuse.com/broadcastchannel
		if (message === BROADCAST_MESSAGES.USER_CLOSE_ONBOARDING_MODAL) {
			externalProps?.setShowModal?.(false)
			closeModal()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [message])

	const isOnboardedToBuildersNetworkHandler = useCallback(() => {
		isInExoSkeleton() &&
			startIsOnboardedAPITracking &&
			startIsOnboardedAPITracking()
		dispatch(showLoadingState(true))

		NapiClient.isOnboardedToBuildersNetwork()
			.then(async (response: OnboardedBuilderNetworkResponse) => {
				if (!response.isOnboarded) {
					await getSelectionsListJobTitleandCompanyType()
					response.avatarUrl = eliminateDefaultXAvatar(response.avatarUrl)

					const userResponse = {
						...response,
						title: response?.title?.trim(),
						updatedAvatarUrl: response?.avatarUrl as string,
						phone: response?.phone ?? '',
					}
					dispatch(setUserInfo(userResponse))
					dispatch(setImmutableUserInfo(userResponse))
					if (!response?.bcUserId) {
						track(ANALYTICS_TYPES.NAPI_USER_IN_ACC_WITHOUT_BC_USER_ID, {
							package: name,
							component: OnboardingUserModalApp.name,
							version,
							flowType: ONBOARDING_FLOW,
						})
					}
					// Make sure this line is not executed otherwise onHideCallback will invoke openModal
					startPendoGuidesPolling?.()
				} else {
					// Close modal as the user is already onboarded
					externalProps?.setShowModal?.(false)
					closeModal()
				}
			})
			.catch((e: Error) => {
				// Catch errors to prevent blocking user flow, but do notify bugsnag
				BugsnagNotify(e, {
					message:
						'Network Onboarding API (isOnboardedToBuildersNetwork) Error',
				})
			})
			.finally(() => {
				dispatch(showLoadingState(false))
				isInExoSkeleton() &&
					endIsOnboardedAPITracking &&
					endIsOnboardedAPITracking()
			})
	}, [dispatch, openModal])

	const getSelectionsListJobTitleandCompanyType = useCallback(async () => {
		try {
			const response = await NapiClient.getSelectionListByName(
				`${SELECTION_LIST_KEYS.JOB_TITLES},${SELECTION_LIST_KEYS.COMPANY_TYPES}`
			)
			dispatch(setSelectionsList(response))
		} catch (e) {
			track(ANALYTICS_TYPES.NETWORK_ONBOARDING_API_ERROR, {
				package: name,
				component: OnboardingUserModalApp.name,
				version,
				isConfigEmpty: _.isEmpty(config),
				apiName: 'jobTitlesAndCompanyType',
			})
			BugsnagNotify(e as Error, {
				message: 'Network Onboarding API (getSelectionListByName) Error',
			})
			throw e
		}
	}, [])

	React.useEffect(() => {
		if (napiUserOnboarding && externalProps?.showModal) {
			dispatch(setFlowType(ONBOARDING_FLOW))
			dispatch(setAppConfig(config))
			track(ANALYTICS_TYPES.NAPI_ONBOARDED_MODAL_MOUNT, {
				package: name,
				component: OnboardingUserModalApp.name,
				version,
			})

			isOnboardedToBuildersNetworkHandler()
			return () => {
				track(ANALYTICS_TYPES.NAPI_ONBOARDED_MODAL_UNMOUNT, {
					package: name,
					component: OnboardingUserModalApp.name,
					version,
				})
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [napiUserOnboarding, config])

	return (
		<StepModal
			steps={[ProfileEditAndConfirmationStep(), ProfilePictureStep()]}
			styles={{
				overlayStyle: {
					// Allowing the modal to be on top of the other elements
					zIndex: 1000000,
				},
			}}
		/>
	)
}

const OnboardingACCUserModal: React.FC<any> = ({
	config,
	showModal,
	setShowModal,
	onSuccess,
}: {
	config: Config
	showModal: boolean
	setShowModal: React.Dispatch<React.SetStateAction<boolean>>
	onSuccess: () => void
}): JSX.Element | null => {
	const OnboardingACCUserModalWithProviders = useMemo(
		() =>
			withMultipleProviders(OnboardingUserModalApp, [
				{
					provider: OnboardingUserAppProvider,
					props: {
						externalProps: {
							setShowModal,
							showModal,
							onSuccess,
						},
					},
				},
				{
					provider: FeatureFlagProvider,
					props: {
						queryParams: {
							appType: 'onboarding',
							flags: 'napiUserOnboarding',
							requestPath: window?.location?.pathname,
						},
					},
				},
			]),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)
	return showModal ? (
		<ConditionalErrorBoundary
			componentName={name}
			componentVersion={version}
			ErrorModal={ErrorModal}
		>
			<OnboardingACCUserModalWithProviders config={config} />
		</ConditionalErrorBoundary>
	) : null
}

export default withConfig(
	withStepModalProvider(
		withBugsnag(OnboardingACCUserModal, displayName, version)
	)
)
